import { useCallback, useEffect, useRef } from 'react';

import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';

import { type AdsGramController, type ShowPromiseResult } from '@/app/types/adsgram';

import { useUser } from '@/entities/user/model/user.store';

import { resolveAdsGramInstance } from '../helpers/resolve-ads-gram-instance';
import { CustomErrorCode } from './types';

export interface useAdsgramParams {
  onReward?: (result: ShowPromiseResult) => void;
  onError?: (result: ShowPromiseResult) => void;

  onSkip?: () => void;
}

const blockId = import.meta.env.VITE_ADS_GRAM_KEY;

export const useAdsgram = ({ onReward, onError, onSkip }: useAdsgramParams = {}) => {
  const { t } = useTranslation();

  const user = useUser();

  const controllerRef = useRef<AdsGramController | undefined>(undefined);

  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  const makeError = useCallback(
    (
      state: ShowPromiseResult['state'],
      description: string,
      code?: string
    ): ShowPromiseResult => {
      return {
        error: true,
        done: false,
        state,
        description,
        code,
      };
    },
    []
  );

  const handleStart = useCallback(() => {
    // store prev isVisible of BackButton to display it correctly after ads
    backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;

    if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();

    // Fix safe area padding for adsgram content
    if (blockId) {
      const adContainer = document.getElementById(blockId);

      if (!adContainer) return;

      adContainer.style.setProperty(
        '--adsgram-content-safe-area-top',
        'var(--tg-content-safe-area-inset-top, 0px)'
      );
      adContainer.style.setProperty(
        '--adsgram-safe-area-top',
        'var(--tg-safe-area-inset-top, 0px)'
      );

      adContainer.style.setProperty(
        '--adsgram-content-safe-area-bottom',
        'var(--tg-content-safe-area-inset-bottom, 0px)'
      );
      adContainer.style.setProperty(
        '--adsgram-safe-area-bottom',
        'var(--tg-safe-area-inset-bottom, 0px)'
      );

      adContainer.style.setProperty(
        '--adsgram-content-safe-area-left',
        'var(--tg-content-safe-area-inset-left, 0px)'
      );
      adContainer.style.setProperty(
        '--adsgram-safe-area-left',
        'var(--tg-safe-area-inset-left, 0px)'
      );

      adContainer.style.setProperty(
        '--adsgram-content-safe-area-right',
        'var(--tg-content-safe-area-inset-right, 0px)'
      );
      adContainer.style.setProperty(
        '--adsgram-safe-area-right',
        'var(--tg-safe-area-inset-right, 0px)'
      );
    }
  }, []);

  const handleEnd = useCallback(() => {
    if (backButtonWasVisibleRef.current) {
      WebApp.BackButton.show();
      backButtonWasVisibleRef.current = true;
    }
  }, []);

  const handleAdsNotFound = useCallback(() => {
    onError?.(
      makeError(
        'render',
        t('Oops, no ads available right now'),
        CustomErrorCode.NO_AVAILABLE_ADS
      )
    );
  }, [makeError, onError, t]);

  const initController = useCallback(async () => {
    if (controllerRef.current) return controllerRef.current;

    const adsgram = await resolveAdsGramInstance();

    if (!adsgram || !blockId || !user) return undefined;

    // WebApp.initDataUnsafe.user.id might be undefined, but it's required for AdsGram
    if (!WebApp?.initDataUnsafe?.user?.id) {
      WebApp.initDataUnsafe.user = {
        ...WebApp.initDataUnsafe.user,
        first_name: WebApp.initDataUnsafe.user?.first_name ?? user.firstName,
        id: user.telegramId,
      };
    }

    const controller = adsgram.init({
      blockId,
      debug: import.meta.env.DEV,
      debugBannerType: 'FullscreenMedia',
    });

    controllerRef.current = controller;

    controller.addEventListener('onStart', handleStart);

    controller.addEventListener('onError', handleEnd);
    controller.addEventListener('onComplete', handleEnd);

    return controller;
  }, [handleEnd, handleStart, user]);

  useEffect(() => {
    void (async () => {
      const controller = await initController();

      if (controller) {
        controllerRef.current = controller;

        controllerRef.current.addEventListener('onStart', handleStart);
        controllerRef.current.addEventListener('onBannerNotFound', handleAdsNotFound);

        controllerRef.current.addEventListener('onError', handleEnd);
        controllerRef.current.addEventListener('onComplete', handleEnd);
      }
    })();

    return () => {
      controllerRef.current?.removeEventListener('onStart', handleStart);
      controllerRef.current?.removeEventListener('onBannerNotFound', handleAdsNotFound);

      controllerRef.current?.removeEventListener('onError', handleEnd);
      controllerRef.current?.removeEventListener('onComplete', handleEnd);

      controllerRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const show = useCallback(async () => {
    const controller = await initController();

    /**
     * The `receiveEvent` function is sometimes overwritten or removed after showing AdsGram ads.
     * This issue occurs because the AdsGram script or other external factors may replace or delete
     * `receiveEvent` from the global scope, causing event handling to break.
     *
     * To prevent this, we capture the original `receiveEvent` references and restore them
     * if they are modified or lost after an ad is displayed.
     */
    const eventPaths: string[][] = [
      ['TelegramGameProxy', 'receiveEvent'], // Telegram Desktop
      ['Telegram', 'WebView', 'receiveEvent'], // Telegram for iOS and Android
      ['TelegramGameProxy_receiveEvent'], // Windows Phone
    ];

    const getNestedProperty = (path: string[]) =>
      path.reduce(
        (obj, key) => (obj && (obj as Record<string, any>)[key]) ?? undefined,
        window
      );

    // Capture original receiveEvent references
    const originalEvents = eventPaths.map(getNestedProperty);

    return new Promise<ShowPromiseResult>((resolve, reject) => {
      if (!controller) {
        const error = makeError(
          'load',
          blockId ? 'Adsgram script not loaded' : 'Block ID is not provided'
        );

        onError?.(error);

        reject(error);
        return;
      }

      controller
        .show()
        .then((res) => {
          // Restore any overwritten receiveEvent functions
          eventPaths.forEach((path, i) => {
            const currentReceiveEvent = getNestedProperty(path);

            if (typeof currentReceiveEvent !== 'function') {
              // Reassign the original function
              let target = window as Record<string, any>;

              for (let j = 0; j < path.length - 1; j++) {
                // Ensure object exists
                if (!target[path[j]]) target[path[j]] = {};

                target = target[path[j]];
              }

              target[path[path.length - 1]] = originalEvents[i];

              console.warn(`Restored receiveEvent at path: ${path.join('.')}`);
            }
          });

          if (res.done) {
            onReward?.(res);
          } else {
            onSkip?.();
          }

          resolve(res);
        })
        .catch((err: ShowPromiseResult) => {
          console.error(err);
          onError?.(err);

          reject(err.description);
        });
    });
  }, [initController, makeError, onError, onReward, onSkip]);

  return show;
};
